import WarningIcon from '@mui/icons-material/Warning'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import CheckIcon from '@mui/icons-material/Check'
import ArchiveIcon from '@mui/icons-material/Archive'
import CachedIcon from '@mui/icons-material/Cached'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import React, { FC } from 'react'
import { theme } from '~/lib/theme'
import { Stack, styled, Tab, Tabs, useMediaQuery } from '@mui/material'

export const TabState = {
  Running: 'running',
  Closed: 'closed',
  WaitingForReview: 'waitingForReview',
  RevisionRequired: 'revisionRequired',
  RegularPublished: 'regularPublished',
  WaitingForPublication: 'waitingForPublication',
} as const

export type TabState = (typeof TabState)[keyof typeof TabState]

export const TabStateLabel = {
  [TabState.Running]: '公開中',
  [TabState.Closed]: 'アーカイブ',
  [TabState.WaitingForReview]: '審査中',
  [TabState.RevisionRequired]: '差し戻し',
  [TabState.RegularPublished]: '定期公開中',
  [TabState.WaitingForPublication]: '公開待機中',
}

type Props = {
  tabState: TabState
  handleTabChange: (tabValue: TabState) => void
  loading: boolean
  children: React.ReactNode
}

export const WaitingCampaignsTab: FC<Props> = (props) => {
  return (
    <Stack gap={1}>
      <SelectCampaignsStatusTabs
        value={props.tabState}
        onChange={(_e, newValue) => props.handleTabChange(newValue)}
        aria-label="SelectCampaignsStatusTag"
        // Tabごとに色を変えるためデフォルトのStyleを消す
        TabIndicatorProps={{ sx: { display: 'none' } }}
      >
        <SelectCampaignsStatusTab
          icon={<WarningIcon />}
          value={TabState.RevisionRequired}
          label={TabStateLabel[TabState.RevisionRequired]}
          disabled={props.loading}
          sx={{
            '&.Mui-selected': {
              color: 'error.main',
              borderBottom: `2px solid ${theme.palette.error.main}`,
            },
          }}
        />
        <SelectCampaignsStatusTab
          icon={<HourglassEmptyIcon />}
          value={TabState.WaitingForPublication}
          label={TabStateLabel[TabState.WaitingForPublication]}
          disabled={props.loading}
          sx={{
            '&.Mui-selected': {
              color: 'success.main',
              borderBottom: `2px solid ${theme.palette.success.main}`,
            },
          }}
        />
        <SelectCampaignsStatusTab
          icon={<ContentPasteIcon />}
          value={TabState.WaitingForReview}
          label={TabStateLabel[TabState.WaitingForReview]}
          disabled={props.loading}
          sx={{
            '&.Mui-selected': {
              borderBottom: `2px solid ${theme.palette.primary.main}`,
            },
          }}
        />
      </SelectCampaignsStatusTabs>
      {props.children}
    </Stack>
  )
}

export const ApprovedCampaignsTab: FC<Props> = (props) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Stack gap={1}>
      <SelectCampaignsStatusTabs
        value={props.tabState}
        onChange={(_e, newValue) => props.handleTabChange(newValue)}
        aria-label="SelectCampaignsStatusTag"
      >
        <SelectCampaignsStatusTab
          icon={<CheckIcon />}
          value={TabState.Running}
          // モバイル表示時に文字数を減らして、UIの文字の折り返しを防ぐための分岐
          label={isMobile ? TabStateLabel[TabState.Running] : `${TabStateLabel[TabState.Running]}のキャンペーン`}
          disabled={props.loading}
        />
        <SelectCampaignsStatusTab
          icon={<ArchiveIcon />}
          value={TabState.Closed}
          label={TabStateLabel[TabState.Closed]}
          disabled={props.loading}
        />
        <SelectCampaignsStatusTab
          icon={<CachedIcon />}
          value={TabState.RegularPublished}
          // モバイル表示時に文字数を減らして、UIの文字の折り返しを防ぐための分岐
          label={isMobile ? TabStateLabel[TabState.RegularPublished] : '定期公開設定中'}
          disabled={props.loading}
        />
      </SelectCampaignsStatusTabs>
      {props.children}
    </Stack>
  )
}

const SelectCampaignsStatusTabs = styled(Tabs)`
  &.MuiTabs-root {
    border-bottom: 1px solid #e8e8e8;
  }
`
const SelectCampaignsStatusTab = styled(Tab)`
  &.MuiTab-root {
    text-transform: none;
    font-weight: ${theme.typography.fontWeightRegular};
    flex: 1;

    &:hover {
      opacity: 1;
    }
  }
  &.Mui-selected {
    font-weight: ${theme.typography.fontWeightMedium};
  }
`
